import { createSlice } from '@reduxjs/toolkit';
import { dispatch } from '../store';
import AxiosService from '../../services/axios-service';
import { removeCpfOrCnpjMask } from '../../utils/utils';

export const STATUS = {
	ATIVO: 1,
	CANCELADO: 2,
	RECUSADO: 3,
	ACEITO: 4,
	EXPIRADO: 5,
	REALIZADO: 6,
	CONFIRMADO: 7,
	PAGO: 8
}

export const TAG = {
	NOVO: 1,
	AGUARDANDO_MATCH: 2,
	AGENDADO: 3,
	REAGENDADO: 4,
	CANCELADO_PELO_CLIENTE: 5,
	CANCELADO_PELA_AGYX: 6,
	REALIZADO: 7,
	CONCLUIDO: 8,
	PAGO: 9,
	AVALIADO: 10,
	NAO_AVALIADO: 11,
	SEGUNDO_MATCH: 12,
	EXPIRADO: 13
}

const initialState = {
	isLoading: false,
	error: false,
	pedidos: null,
	pedido: [],
	instalador: {},
	pedidoIdAtual: null,
	requisicaoAtiva: null,
};

const slice = createSlice({
	name: 'pedidos',
	initialState: initialState,
	reducers: {
		startLoading(state) {
			state.isLoading = true;
		},
		hasError(state, action) {
			state.isLoading = false;
			state.error = action.payload;
		},
		getPedidosSuccess(state, action) {
			state.isLoading = false;
			state.pedidos = action.payload;
		},
		getPedidoDetalheSuccess(state, action) {
			state.isLoading = false;
			state.pedido = action.payload;
		},
		setPedidoIdAtual(state, action) {
			state.pedidoIdAtual = action.payload;
		},
		getRequisicaoAtivaSuccess(state, action) {
			state.isLoading = false;
			state.requisicaoAtiva = action.payload;
		},
		getInstaladorAtivoSuccess(state, action) {
			state.isLoading = false;
			state.instalador = action.payload;
		},
		avaliarPedidoSuccess(state, action) {
			state.isLoading = false;
		},
	}
});

export default slice.reducer;

export const { setPedidoIdAtual } = slice.actions;

export function getStatusPedido(tagId) {
	if (tagId === TAG.NOVO) return 'Aguardando agendamento';
	if (tagId === TAG.AGUARDANDO_MATCH || tagId === TAG.EXPIRADO) return 'Aguardando instalador';
	if (tagId === TAG.AGENDADO || tagId === TAG.SEGUNDO_MATCH || tagId === TAG.REAGENDADO) return 'Agendado';
	if (tagId === TAG.REALIZADO || tagId === TAG.CONCLUIDO || tagId === TAG.PAGO || tagId === TAG.AVALIADO || tagId === TAG.NAO_AVALIADO) return 'Realizado';
	if (tagId === TAG.CANCELADO_PELA_AGYX || tagId === TAG.CANCELADO_PELO_CLIENTE) return 'Cancelado';
	return ''
}

export function getImagePedido(tagId) {
	if (tagId === TAG.NOVO) return '/images/ar1.jpg';
	if (tagId === TAG.AGUARDANDO_MATCH || tagId === TAG.EXPIRADO) return '/images/ar2.jpg';
	if (tagId === TAG.AGENDADO || tagId === TAG.SEGUNDO_MATCH || tagId === TAG.REAGENDADO) return '/images/ar3.jpg';
	if (tagId === TAG.REALIZADO || tagId === TAG.CONCLUIDO || tagId === TAG.PAGO || tagId === TAG.AVALIADO || tagId === TAG.NAO_AVALIADO) return '/images/ar4.jpg';
	if (tagId === TAG.CANCELADO_PELA_AGYX || tagId === TAG.CANCELADO_PELO_CLIENTE) return '/images/ar5.jpg';
	return ''
}

export async function getPedidoById(idPedido) {
	try {
		const axiosService = new AxiosService();
		const response = await axiosService.get(`pedido/${idPedido}`);
		return response[0];
	} catch (error) {
		console.error(error);
		return {};
	}
}

export async function getAgendamentoByIdPedido(idPedido) {
	try {
		const axiosService = new AxiosService();
		const response = await axiosService.get(`pedido-agendamento/filtro?target[]=pedido_id&target_value[]=${idPedido}`);
		return response[0];
	} catch (error) {
		console.error(error);
		return {};
	}
}

export async function getitensPedidoByIdPedido(idPedido) {
	try {
		const axiosService = new AxiosService();
		const response = await axiosService.get(`pedido-itens?target[]=pedido_id&target_value[]=${idPedido}`);
		response.forEach((item) => {
			delete item.produto_id;
			delete item.quantidade;
			delete item.status;
		});
		return response;
	} catch (error) {
		console.error(error);
		return [];
	}
}

export async function getEstadoById(id) {
	try {
		const axiosService = new AxiosService();
		const response = await axiosService.get(`localizacao/estado?target[]=id&target_value[]=${id}`);
		return response[0];
	} catch (error) {
		console.error(error);
		return {};
	}
}

export async function getCidadeById(id) {
	try {
		const axiosService = new AxiosService();
		const response = await axiosService.get(`localizacao/cidade?target[]=id&target_value[]=${id}`);
		return response[0];
	} catch (error) {
		console.error(error);
		return {};
	}
}

export async function atualizarPedido(dados) {
	try {
		const { pedidoId, endereco, informacoesAdicionais, prontoParaMatch } = dados;
		const { cep, logradouro, numero, complemento, bairro, estado, cidade } = endereco;
		const body = {
			id: pedidoId,
			cep: cep,
			logradouro: logradouro,
			numero: numero,
			complemento: complemento,
			bairro: bairro,
			estado: estado,
			cidade: cidade,
			descricao: informacoesAdicionais,
			pronto_para_match: prontoParaMatch,
		};

		const axiosService = new AxiosService();
		const response = await axiosService.put(`pedido`, body);
		return response;
	} catch (error) {
		console.error(error);
		return {};
	}
}

export async function atualizarAgendamento(dados) {
	try {
		const body = {
			id: dados.agendamentoId,
			pedido_id: dados.pedidoId,
			data: dados.data,
			turno: dados.turno,
			observacoes: dados.informacoesAdicionais,
			instalador_id: dados?.tec?.id || null
		};

		const axiosService = new AxiosService();
		const response = await axiosService.put(`pedido-agendamento`, body);
		return response;
	} catch (error) {
		console.error(error);
		return {};
	}
}

export async function matchDoCliente(dados){
	try {
		const axiosService = new AxiosService();
		const response = await axiosService.post(`cliente/matchCliente`, dados);
		return response;
	} catch (error) {
		console.error(error);
		return {};
	}
}

export async function salvarAgendamento(dados) {
	await atualizarPedido(dados);
	await atualizarAgendamento(dados);
	const hostname = window.location.hostname;
	let origemAgendamento = 'Portal do Vendedor/Cliente';
  	if (hostname.startsWith('vendedor')) {
    origemAgendamento = 'Portal do Vendedor';
  	} else if (hostname.startsWith('cliente')) {
    origemAgendamento = 'Portal do Cliente';
	}
	await matchDoCliente({ pedidoId: dados.pedidoId, agendamentoId: dados.agendamentoId, tec: dados.tec, itens: dados.itens, origem_agendamento: origemAgendamento });
}


export async function getMarcas() {
	try {
		const axiosService = new AxiosService();
		const response = await axiosService.get(`marca`);
		return response;
	} catch (error) {
		console.error(error);
		return [];
	}
}

export async function getModelos() {
	try {
		const axiosService = new AxiosService();
		const response = await axiosService.get(`modelo`);
		return response;
	} catch (error) {
		console.error(error);
		return [];
	}
}

export async function getPotencias() {
	try {
		const axiosService = new AxiosService();
		const response = await axiosService.get(`potencia`);
		return response;
	} catch (error) {
		console.error(error);
		return [];
	}
}

export async function getInstaladoresByInformacoesDoPedido(pedidoId) {
	try {
		const axiosService = new AxiosService();
		const response = await axiosService.get(`cliente/allInstaladores?pedidoId=${pedidoId}`);
		return response;
	} catch (error) {
		console.error(error);
		return [];
	}
}

export async function getTiposServico() {
	try {
		const axiosService = new AxiosService();
		const response = await axiosService.get(`tipo-servico`);
		return response;
	} catch (error) {
		console.error(error);
		return [];
	}
}

export async function sendPushNotification(title, body, topic) {
	try {
		const axiosService = new AxiosService();
		const response = await axiosService.get(`comunicacao/push?title=${title}&body=${body}&ids[]=${topic}`);
		return response;
	} catch (error) {
		console.error(error);
		return [];
	}
}

//--------- funçoes com dispath

export function getPedidos(cpf) {
	return async () => {
		try {
			dispatch(slice.actions.startLoading());
			cpf = removeCpfOrCnpjMask(cpf);
			const axiosService = new AxiosService();
			const response = await axiosService.get('pedido/selectByCpf/' + encodeURIComponent(cpf));
			if (response.length > 0) {
				dispatch(slice.actions.getPedidosSuccess(response));
			} else {
				dispatch(slice.actions.getPedidosSuccess(null));
			}
		} catch (error) {
			console.error(error)
			dispatch(slice.actions.hasError(error));
		}
	};
}

export function getPedidoDetalhe(pedido_id) {
	return async () => {
		try {
			dispatch(slice.actions.startLoading());
			const axiosService = new AxiosService();
			const response = await axiosService.get(`pedido/selectPedidoDetalhe/${pedido_id}`);
			dispatch(slice.actions.getPedidoDetalheSuccess(response));
		} catch (error) {
			console.error(error)
			dispatch(slice.actions.hasError(error));
		}
	};
}

export function getRequisicaoAtivaPedido(pedido_id) {
	return async () => {
		try {
			dispatch(slice.actions.startLoading());
			const axiosService = new AxiosService();
			const response = await axiosService.get('pedido-requisicao/pedido/' + pedido_id);
			dispatch(slice.actions.getRequisicaoAtivaSuccess(response[0].id));
		} catch (error) {
			console.error(error)
			dispatch(slice.actions.hasError(error));
		}
	};
}

export function getInstaladorAtivo(pedidoId) {
	return async () => {
		try {
			dispatch(slice.actions.startLoading());
			const axiosService = new AxiosService();
			const response = await axiosService.get('pedido-requisicao/instalador/' + pedidoId);
			dispatch(slice.actions.getInstaladorAtivoSuccess(response[0]));
		} catch (error) {
			console.error(error);
			dispatch(slice.actions.hasError(error));
		}
	};
}

export function avaliarPedido(pedido, rating, title, description) {
	return async () => {
		try {
			dispatch(slice.actions.startLoading());
			const body = {
				pedido_id: pedido.id,
				requisicao_id: pedido.requisicao_id,
				instalador_id: pedido.instalador_id,
				pedido_status_id: STATUS.CONFIRMADO,
				tag_id: TAG.AVALIADO,
				avaliacao_nota: rating,
				avaliacao_comentario: description,
				avaliacao_titulo: title
			};

			const axiosService = new AxiosService();
			const response = await axiosService.post(`pedido/avaliar`, body);
			dispatch(slice.actions.avaliarPedidoSuccess(response));
		} catch (error) {
			console.error(error)
			const errorMessage = error?.response?.data?.message || error?.message || 'Erro ao avaliar pedido';
			dispatch(slice.actions.hasError({status: 'error', message: errorMessage}));
		}
	};
}
